import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ensureUser,
  fetchBudget,
  fetchAccounts,
  fetchTxns,
  createTxn,
  createAccount,
  createCategory,
  assign,
  ping,
  type Budget,
  type Account,
  type Txn,
  type CategoryMonth,
} from "./lib/api";

const fmt = (sen: number) =>
  `${sen < 0 ? "-" : ""}RM ${(Math.abs(sen) / 100).toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabel = (m: string) => {
  const [y, mm] = m.split("-").map(Number);
  return `${MONTH_NAMES[mm - 1]} ${y}`;
};
const shiftMonth = (m: string, delta: number) => {
  const [y, mm] = m.split("-").map(Number);
  const d = new Date(Date.UTC(y, mm - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

function availColor(c: CategoryMonth): string {
  if (c.available < 0) return "var(--red)";
  if (c.isCcPayment) return "var(--blue)";
  if (c.available === 0 && c.assigned === 0) return "var(--muted)";
  return "var(--green)";
}

export default function App() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [budget, setBudget] = useState<Budget | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [status, setStatus] = useState<"boot" | "ready" | "error">("boot");
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"budget" | "accounts">("budget");
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showAddAcct, setShowAddAcct] = useState(false);

  const boot = useCallback(async () => {
    setStatus("boot");
    const up = await ping();
    if (!up) {
      setError("api.bajetly.com unreachable");
      setStatus("error");
      return;
    }
    try {
      await ensureUser();
      setStatus("ready");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus("error");
    }
  }, []);

  useEffect(() => { boot(); }, [boot]);

  const reload = useCallback(async (m: string) => {
    try {
      const [b, a, t] = await Promise.all([fetchBudget(m), fetchAccounts(), fetchTxns()]);
      setBudget(b);
      setAccounts(a.accounts);
      setTxns(t.transactions);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    if (status === "ready") reload(month);
  }, [status, month, reload]);

  /** Edit an Assigned cell: set the month's TOTAL for that category. */
  const saveAssignment = async (categoryId: string, raw: string) => {
    setEditing(null);
    if (!budget) return;
    const clean = raw.replace(/[^0-9.\-]/g, "");
    if (clean === "" || isNaN(Number(clean))) return reload(month);
    const sen = Math.round(parseFloat(clean) * 100);
    try {
      await assign(month, categoryId, sen);
      await reload(month);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const groups = useMemo(() => {
    if (!budget) return [];
    const regular = budget.categories.filter((c) => !c.isCcPayment);
    const payments = budget.categories.filter((c) => c.isCcPayment);
    const byGroup = new Map<string, CategoryMonth[]>();
    for (const c of regular) {
      const key = c.groupId ?? "Ungrouped";
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key)!.push(c);
    }
    const out = [...byGroup.entries()].map(([g, cats]) => ({ title: g, cats }));
    if (payments.length) out.push({ title: "Credit Card Payments", cats: payments });
    return out;
  }, [budget]);

  if (status === "boot") {
    return <div className="center">Connecting…</div>;
  }
  if (status === "error") {
    return (
      <div className="center">
        <p>⚠️ {error}</p>
        <button className="fund-all" onClick={boot}>Retry</button>
      </div>
    );
  }

  const acctName = (id: string) => accounts.find((a) => a.id === id)?.name ?? "?";
  const catName = (id: string | null) =>
    budget?.categories.find((c) => c.categoryId === id)?.categoryName ?? "—";

  return (
    <div className="app">
      <header className="masthead">
        <div className="wordmark">Bajetly</div>
        <nav className="tabs">
          <button className={view === "budget" ? "active" : ""} onClick={() => setView("budget")}>Budget</button>
          <button className={view === "accounts" ? "active" : ""} onClick={() => setView("accounts")}>Accounts</button>
        </nav>
      </header>

      {error && <div className="banner">⚠️ {error}</div>}

      {view === "budget" && budget && (
        <>
          <div className="month-nav">
            <button onClick={() => setMonth(shiftMonth(month, -1))} aria-label="previous month">‹</button>
            <span className="month-label">{monthLabel(month)}</span>
            <button onClick={() => setMonth(shiftMonth(month, 1))} aria-label="next month">›</button>
          </div>

          <div className="to-budget">
            <div>
              <div className="label">
                {budget.toBeBudgeted < 0 ? "Overspent last month" : "Ready to assign"}
              </div>
              <div className={`amount${budget.toBeBudgeted === 0 ? " zero" : ""}${budget.toBeBudgeted < 0 ? " neg" : ""}`}>
                {fmt(budget.toBeBudgeted)}
              </div>
            </div>
            <div className="income-note">
              Income this month: <strong>{fmt(budget.income)}</strong>
            </div>
          </div>

          <div className="budget-table" role="table">
            <div className="brow bhead" role="row">
              <span>Category</span>
              <span className="num">Assigned</span>
              <span className="num">Activity</span>
              <span className="num">Available</span>
            </div>

            {groups.map((g) => (
              <div key={g.title}>
                <div className="brow group-row"><span>{g.title}</span></div>
                {g.cats.map((c) => (
                  <div className="brow" role="row" key={c.categoryId}>
                    <span className="catname">
                      {c.categoryName}
                      {c.overspentReset && c.priorAvailable < 0 && (
                        <span className="flag" title="overspent last month — reset"> ↩</span>
                      )}
                    </span>

                    <span
                      className="num assigned-cell"
                      onClick={() => setEditing({ id: c.categoryId, value: (c.assigned / 100).toFixed(2) })}
                      title="click to edit assigned"
                    >
                      {editing?.id === c.categoryId ? (
                        <input
                          autoFocus
                          defaultValue={editing.value}
                          onBlur={(e) => saveAssignment(c.categoryId, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                            if (e.key === "Escape") setEditing(null);
                          }}
                        />
                      ) : (
                        fmt(c.assigned)
                      )}
                    </span>

                    <span className="num muted">{fmt(c.activity)}</span>
                    <span className="num" style={{ color: availColor(c), fontWeight: 600 }}>
                      {fmt(c.available)}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="row-actions">
            <button className="ghost" onClick={async () => {
              const name = prompt("Category name:");
              if (name) { await createCategory(name); reload(month); }
            }}>+ Category</button>
          </div>
        </>
      )}

      {view === "accounts" && (
        <>
          <div className="section-head">
            <h2>Accounts</h2>
            <button className="ghost" onClick={() => setShowAddAcct(true)}>+ Add account</button>
          </div>

          {accounts.map((a) => {
            const bal = budget?.accounts.find((x) => x.accountId === a.id)?.balance ?? 0;
            return (
              <div className="acct-row" key={a.id}>
                <span>
                  {a.kind === "credit_card" ? "💳" : a.kind === "savings" ? "🏦" : "💰"} {a.name}
                  <span className="muted small"> {a.kind.replace("_", " ")}</span>
                </span>
                <span className={`num${bal < 0 ? " neg" : ""}`}>{fmt(bal)}</span>
              </div>
            );
          })}

          <div className="section-head">
            <h2>Transactions</h2>
            <button className="ghost" onClick={() => setShowAdd(true)}>+ Add</button>
          </div>

          <div className="txn-list">
            {txns.length === 0 && <p className="muted small">No transactions yet.</p>}
            {txns.map((t) => (
              <div className="txn-row" key={t.id}>
                <span className="small muted">{t.date}</span>
                <span className="txn-payee">{t.payee ?? "(transfer)"}</span>
                <span className="small muted">{acctName(t.account_id)}{t.category_id ? ` · ${catName(t.category_id)}` : ""}</span>
                <span className={`num${t.amount < 0 ? " neg" : ""}`}>{fmt(t.amount)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {showAdd && (
        <AddTxnModal
          accounts={accounts}
          categories={budget?.categories.filter((c) => !c.isCcPayment) ?? []}
          onClose={() => setShowAdd(false)}
          onSave={async (b) => {
            await createTxn(b);
            setShowAdd(false);
            reload(month);
          }}
        />
      )}

      {showAddAcct && (
        <AddAccountModal
          onClose={() => setShowAddAcct(false)}
          onSave={async (b) => {
            await createAccount(b);
            setShowAddAcct(false);
            reload(month);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------ modals */

function AddTxnModal({
  accounts, categories, onClose, onSave,
}: {
  accounts: Account[];
  categories: CategoryMonth[];
  onClose: () => void;
  onSave: (b: { date: string; accountId: string; categoryId?: string | null; amountSen: number; payee?: string }) => void;
}) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? "");
  const [categoryId, setCategoryId] = useState<string>("");
  const [payee, setPayee] = useState("");
  const [amount, setAmount] = useState("");
  const [outflow, setOutflow] = useState(true);

  const canSave = accountId && amount && !isNaN(parseFloat(amount));

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>New transaction</h3>
        <label>Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label>Account
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label>Payee<input value={payee} onChange={(e) => setPayee(e.target.value)} placeholder="Jaya Grocer" /></label>
        <label>Category
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">— none (income/transfer) —</option>
            {categories.map((c) => <option key={c.categoryId} value={c.categoryId}>{c.categoryName}</option>)}
          </select>
        </label>
        <div className="split">
          <label className="grow">Amount (RM)
            <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="25.00" />
          </label>
          <label className="dir">
            <input type="checkbox" checked={outflow} onChange={(e) => setOutflow(e.target.checked)} />
            outflow
          </label>
        </div>
        <div className="modal-actions">
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="fund-all" disabled={!canSave} onClick={() => {
            const sen = Math.round(parseFloat(amount) * 100);
            onSave({
              date, accountId,
              categoryId: categoryId || null,
              amountSen: outflow ? -Math.abs(sen) : Math.abs(sen),
              payee: payee || undefined,
            });
          }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function AddAccountModal({
  onClose, onSave,
}: {
  onClose: () => void;
  onSave: (b: { name: string; kind: string; balanceSen?: number }) => void;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("checking");
  const [balance, setBalance] = useState("");
  const isCard = kind === "credit_card";

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>New account</h3>
        <label>Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ryt Checking" /></label>
        <label>Type
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
            <option value="cash">Cash</option>
            <option value="credit_card">Credit card</option>
            <option value="tracking">Tracking (off-budget)</option>
          </select>
        </label>
        <label>{isCard ? "Current balance owed (RM)" : "Current balance (RM)"}
          <input inputMode="decimal" value={balance} onChange={(e) => setBalance(e.target.value)} placeholder="0.00" />
        </label>
        <div className="modal-actions">
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="fund-all" disabled={!name} onClick={() => {
            const sen = balance ? Math.round(parseFloat(balance) * 100) : 0;
            // card balances are stored negative (owed); cash positive
            onSave({ name, kind, balanceSen: isCard ? -Math.abs(sen) : sen });
          }}>Create</button>
        </div>
      </div>
    </div>
  );
}
